/**
 * Weather query — the one deterministic "what is the sky doing here right now" helper,
 * shared by the client (precipitation particles + sky/fog response) and available to the
 * server. Like `waterColumn`, it is a PURE function of the seed-derived sampler + planet def
 * + the wall-clock time, so every client (and the server) agree on the weather without any
 * networked state — the cohesion that lets rain fall in the same place for everyone.
 *
 * A region's weather = its CLIMATE's baseline propensity (wet coasts rain, deserts stay dry,
 * cold poles get snow — read straight from `PlanetSampler.sample` temp/moist) modulated by a
 * slowly-evolving SYNOPTIC field (drifting cloud fronts that form and dissipate over `t`).
 * Cosmetic only: the wind drives the rain SLANT, it never pushes the player.
 *
 * Inputs are PLANET-LOCAL — the unwrapped sphere direction, same space as `sample`. The wind
 * tangent is planet-local too; rotate it into world space by the planet's spin when needed
 * (the same convention as the river flow tangent).
 */
import { SimplexNoise3D } from "./noise.js";
import * as pmath from "./pmath.js";
import { hashSeed } from "./rng.js";
import type { PlanetSampler, PlanetDef, TerrainSample } from "./universe.js";

/** Persistent ground condition left by recent weather — the "aftermath". */
export interface WeatherGround {
  /** Wet/slicked amount, 0 (dry) … 1 (saturated), including thawing mixed snow. */
  wet: number;
  /** Temporary snow amount, 0 (bare) … 1 (deep); independent of climate snow. */
  snow: number;
}

export interface WeatherColumn {
  /** Cloud cover overhead, 0 (clear) … 1 (overcast). */
  cloud: number;
  /** Precipitation intensity, 0 (none) … 1 (downpour). */
  precip: number;
  /** Precipitation type: 0 none, 1 rain, 2 snow. */
  kind: number;
  /** Prevailing-wind tangent (planet-local unit; 0 when calm). Drives the rain slant. */
  windx: number;
  windy: number;
  windz: number;
  /** Wind strength 0..1. */
  windSpeed: number;
  /** Optical depth of the main cloud mass, not display opacity. */
  cloudDepth?: number;
  /** Fraction of precipitation falling as snow; supports mixed precipitation. */
  snowFraction?: number;
  /** Low atmospheric moisture, 0..1; density is chosen by the renderer. */
  mist?: number;
}

const TAU = Math.PI * 2;
/** Full clear→overcast→clear cycle ≈ 15 min, so weather visibly turns over within a session. */
const FRONT_RATE = TAU / 900;
/** Spatial frequency of the cloud-shape field (cycles around the sphere). */
const COVER_FREQ = 2.2;
/** Mean angular transport of regional weather around the spin axis. */
const DRIFT_PERIOD = 18000;
export const WEATHER_DRIFT = TAU / DRIFT_PERIOD;
/** Below this temperature ([-1,1]) precip falls as snow rather than rain. */
const SNOW_TEMP = -0.15;

/** Per-planet noise channels for the synoptic field — built once per seed, then cached. */
interface WeatherNoise {
  warp: SimplexNoise3D;
  cover: SimplexNoise3D;
  front: SimplexNoise3D;
}
const NOISE_CACHE = new Map<string, WeatherNoise>();
function noiseFor(def: PlanetDef): WeatherNoise {
  let n = NOISE_CACHE.get(def.seed);
  if (n === undefined) {
    const base = hashSeed(def.seed);
    n = {
      warp: new SimplexNoise3D(base ^ 0x88),
      cover: new SimplexNoise3D(base ^ 0x99),
      front: new SimplexNoise3D(base ^ 0xaa),
    };
    NOISE_CACHE.set(def.seed, n);
  }
  return n;
}

const SCRATCH: TerrainSample = {
  h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8,
};

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}
function smoothstep(e0: number, e1: number, x: number): number {
  const t = clamp01((x - e0) / (e1 - e0));
  return t * t * (3 - 2 * t);
}

/**
 * Seeded cloud masses rotate about the spin axis with bounded zonal shear,
 * plus a growth/dissipation envelope. Both space and time remain continuous
 * at epoch-scale timestamps, including across drift-clock wraps.
 */
function synopticCover(n: WeatherNoise, def: PlanetDef, dx: number, dy: number, dz: number, t: number): number {
  // A latitude-dependent rate multiplied by Unix time wound the weather into
  // sub-metre stripes: walking 10 cm could change clear sky into a storm, and
  // every weather-driven light flickered with it. Modulo on the final angle
  // bounds the number, but does NOT bound that spatial derivative.
  //
  // Rotate uniformly, then add a bounded periodic shear between wind bands.
  // At a clock wrap the rotation changes by exactly 2π and the shear agrees
  // on both sides, so there is no periodic jump or accumulated spatial winding.
  const [ax, ay, az] = def.spin.axis;
  const lat = dx * ax + dy * ay + dz * az;
  const band = pmath.cos(Math.abs(lat) * Math.PI * 3);
  const driftPhase = (t % DRIFT_PERIOD) * WEATHER_DRIFT;
  const angle = -driftPhase + 0.45 * band * pmath.sin(driftPhase);
  const c = pmath.cos(angle), s = pmath.sin(angle), k = lat * (1 - c);
  const x = dx * c + (ay * dz - az * dy) * s + ax * k;
  const y = dy * c + (az * dx - ax * dz) * s + ay * k;
  const z = dz * c + (ax * dy - ay * dx) * s + az * k;
  dx = x; dy = y; dz = z;
  // Domain warp bends the fronts into organic, non-grid shapes.
  const wf = 1.1;
  const wx = n.warp.fbm(dx * wf, dy * wf, dz * wf, 2) * 0.5;
  const wy = n.warp.fbm(dx * wf + 11.3, dy * wf + 3.1, dz * wf + 7.7, 2) * 0.5;
  const wz = n.warp.fbm(dx * wf + 5.2, dy * wf + 9.4, dz * wf + 1.6, 2) * 0.5;
  const shape =
    n.cover.fbm(dx * COVER_FREQ + wx, dy * COVER_FREQ + wy, dz * COVER_FREQ + wz, 4) * 0.5 + 0.5;
  // Each location ebbs on its own phase → cloudy and clear spells march across the globe.
  const phase = (n.front.fbm(dx * 0.8, dy * 0.8, dz * 0.8, 2) * 0.5 + 0.5) * TAU;
  const ebb = 0.5 + 0.5 * pmath.sin((t * FRONT_RATE) % TAU + phase);
  return clamp01(shape * (0.4 + 0.7 * ebb));
}

/**
 * Fill `out` with the weather at a planet-local unit direction at wall-clock time `t` (seconds).
 * Airless worlds (`atmosphere === null`) report dead-calm zeros.
 */
export function weatherAt(
  sampler: PlanetSampler,
  def: PlanetDef,
  dx: number,
  dy: number,
  dz: number,
  t: number,
  out: WeatherColumn,
): WeatherColumn {
  if (def.atmosphere !== null) sampler.sample(dx, dy, dz, SCRATCH, true);
  return weatherFromClimate(def, dx, dy, dz, t, SCRATCH.temp, SCRATCH.moist, out);
}

/** Same field as weatherAt, with cached static climate for weather maps/history. */
export function weatherFromClimate(
  def: PlanetDef, dx: number, dy: number, dz: number, t: number,
  temp: number, moist: number, out: WeatherColumn,
): WeatherColumn {
  if (def.atmosphere === null) {
    out.cloud = 0;
    out.precip = 0;
    out.kind = 0;
    out.windx = 0;
    out.windy = 0;
    out.windz = 0;
    out.windSpeed = 0;
    out.cloudDepth = out.snowFraction = out.mist = 0;
    return out;
  }

  const humid = clamp01(moist * 0.5 + 0.5); // 0 bone-dry … 1 saturated
  const cover = synopticCover(noiseFor(def), def, dx, dy, dz, t);

  // Cloud forms where the field clears a humidity-lowered threshold: damp regions cloud up
  // easily, deserts need a strong front to cloud at all.
  const thr = 0.6 - humid * 0.32;
  const frontCloud = smoothstep(thr, thr + 0.18, cover);
  // Scattered fair-weather clouds form well before a rain-bearing front. Keep
  // their coverage below the rain threshold and allow genuinely clear spells.
  // Reusing the transported field keeps this layer deterministic and inexpensive.
  const fairCloud = smoothstep(0.14, 0.40, cover) * (0.12 + humid * 0.25);
  const cloud = Math.max(frontCloud, fairCloud);

  // Precipitation needs cloud AND moisture — clouds over a dry region give shade, not rain.
  const wet = clamp01((humid - 0.42) / 0.33);
  // A front forms cloud before it rains. The continuous threshold allows dry
  // overcast and a soft onset; no intensity cutoff at 0.06.
  const maturity = smoothstep(thr + 0.14, thr + 0.34, cover);
  const precip = smoothstep(0.55, 0.95, frontCloud) * maturity * wet;
  const snowFraction = 1 - smoothstep(SNOW_TEMP - 0.07, SNOW_TEMP + 0.07, temp);
  const kind = precip > 0 ? (temp < SNOW_TEMP ? 2 : 1) : 0;

  // Prevailing wind: latitude bands (trade-winds / westerlies / polar easterlies) about the
  // TRUE spin axis. Tangent = local "east" (cross(axis, dir)); the band sign alternates with
  // latitude and the speed eases to zero on the calm lines between bands.
  const [ax, ay, az] = def.spin.axis;
  const lat = Math.abs(dx * ax + dy * ay + dz * az); // 0 equator … 1 pole
  let ex = ay * dz - az * dy;
  let ey = az * dx - ax * dz;
  let ez = ax * dy - ay * dx;
  const el = pmath.hypot(ex, ey, ez);
  const band = pmath.cos(lat * Math.PI * 3); // [-1,1], reverses each band
  if (el > 1e-4) {
    const s = (band >= 0 ? 1 : -1) / el;
    out.windx = ex * s;
    out.windy = ey * s;
    out.windz = ez * s;
  } else {
    out.windx = 0;
    out.windy = 0;
    out.windz = 0;
  }
  out.windSpeed = clamp01(Math.abs(band) * (0.4 + cloud * 0.6)) * (el > 1e-4 ? 1 : 0);
  out.cloud = cloud;
  out.precip = precip;
  out.kind = kind;
  out.snowFraction = snowFraction;
  out.cloudDepth = frontCloud * (0.35 + 2.8 * maturity + precip * 1.2)
    + Math.max(0, fairCloud - frontCloud) * 0.65;
  out.mist = humid * (0.12 + cloud * 0.2 + precip * 0.5);
  return out;
}

// --- Ground aftermath -------------------------------------------------------
// The ground stays wet after rain and snow-covered after snowfall, drying/melting
// gradually rather than flipping with the instantaneous precip. Because `weatherAt`
// is a known function of time, "how wet/snowy is the ground now" is a LEAKY INTEGRAL
// of past precip — computed by sampling backward in time with an exponential kernel.
// Pure + deterministic, so every client agrees without any networked state.
const GROUND_STEP = 30; // seconds between backward samples
const GROUND_K = 40; // sample count → window ≈ GROUND_STEP*K = 20 min
const TAU_WET = 90; // wet dry-out time constant (s)
const TAU_SNOW = 480; // snow melt time constant (s) — snow lingers far longer than rain
const WET_GAIN = 1.4; // scale so moderate rain still visibly slicks the ground
const SNOW_GAIN = 1.6; // snow reads as accumulation, not just a dusting

/**
 * Fill `out` with the deterministic ground aftermath at a planet-local direction and
 * wall-clock time `t`. Mixed precipitation can leave both wetness and snow near
 * the freezing transition. Airless worlds have neither.
 */
export function weatherGroundState(
  sampler: PlanetSampler,
  def: PlanetDef,
  dx: number,
  dy: number,
  dz: number,
  t: number,
  out: WeatherGround,
): WeatherGround {
  if (def.atmosphere !== null) sampler.sample(dx, dy, dz, SCRATCH, true);
  return weatherGroundFromClimate(def, dx, dy, dz, t, SCRATCH.temp, SCRATCH.moist, out);
}

const HISTORY: WeatherColumn = { cloud: 0, precip: 0, kind: 0, windx: 0, windy: 0, windz: 0, windSpeed: 0 };

/** Bounded, deterministic aftermath; caches can reuse climate without terrain queries. */
export function weatherGroundFromClimate(
  def: PlanetDef, dx: number, dy: number, dz: number, t: number,
  temp: number, moist: number, out: WeatherGround,
): WeatherGround {
  if (def.atmosphere === null) {
    out.wet = 0;
    out.snow = 0;
    return out;
  }
  let wet = 0, snow = 0, wetNorm = 0, snowNorm = 0;
  // A mixed phase can leave both snow and wet ground. Warm snow dries/melts
  // faster than cold snow; permanent climate snow remains a separate material.
  const melt = smoothstep(SNOW_TEMP - 0.1, SNOW_TEMP + 0.07, temp);
  const snowTau = TAU_SNOW * (1 - 0.7 * melt);
  for (let i = 0; i < GROUND_K; i++) {
    const w = pmath.exp(-(i * GROUND_STEP) / TAU_WET);
    const s = pmath.exp(-(i * GROUND_STEP) / snowTau);
    weatherFromClimate(def, dx, dy, dz, t - i * GROUND_STEP, temp, moist, HISTORY);
    const f = HISTORY.snowFraction ?? 0;
    wet += HISTORY.precip * (1 - f + f * melt * 0.35) * w;
    snow += HISTORY.precip * f * s * (1 - melt * 0.65);
    wetNorm += w;
    snowNorm += s;
  }
  out.wet = clamp01(wet / wetNorm * WET_GAIN);
  out.snow = clamp01(snow / snowNorm * SNOW_GAIN);
  return out;
}
